import React, { useState, useEffect, useCallback } from 'react';
import { db, functions } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useSkipsData } from '../context/SkipsDataContext';
import ViewToggle from './ViewToggle';
import toast from 'react-hot-toast';

const DriverManagement = () => {
  const { companyId } = useSkipsData();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [maxDrivers, setMaxDrivers] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    displayName: '',
    initialPassword: ''
  });

  // Auto-generate username from displayName + companyId
  const generatedUsername = formData.displayName
    ? `${formData.displayName.replace(/[^a-zA-Z0-9]/g, '')}${companyId || ''}`
    : '';

  // Check if at driver limit
  const currentDriverCount = drivers.length;
  const isAtLimit = maxDrivers !== null && currentDriverCount >= maxDrivers;
  const hasLimit = maxDrivers !== null;

  // Password change state
  const [passwordChangeDriverId, setPasswordChangeDriverId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Load drivers function wrapped in useCallback to satisfy useEffect dependency
  const loadDrivers = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Load company data to get maxDrivers limit
      const companyDocRef = doc(db, 'companies', companyId);
      const companyDoc = await getDoc(companyDocRef);

      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        setMaxDrivers(companyData.maxDrivers || null);
      } else {
        console.warn('Company document not found');
        setMaxDrivers(null);
      }

      // Load drivers
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('companyId', '==', companyId),
        where('role', '==', 'driver')
      );

      const snapshot = await getDocs(q);
      const driversList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setDrivers(driversList);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Load drivers on mount
  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const handleCreateDriver = async (e) => {
    e.preventDefault();

    // Check driver limit
    if (isAtLimit) {
      toast.error('Maximum driver accounts reached. Please contact info@skipeye.com to upgrade your package.');
      return;
    }

    // Validation
    if (!formData.displayName || !formData.initialPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!generatedUsername) {
      toast.error('Driver name is required');
      return;
    }

    if (formData.initialPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      toast.loading('Creating driver account...');

      // Call Cloud Function to create driver
      const createDriverFunc = httpsCallable(functions, 'createDriver');
      await createDriverFunc({
        displayName: formData.displayName,
        username: generatedUsername,
        initialPassword: formData.initialPassword,
        companyId: companyId,
        maxDrivers: maxDrivers
      });

      toast.dismiss();
      toast.success(`Driver account created!\n\nUsername: ${generatedUsername}\nPassword: ${formData.initialPassword}\n\nShare these credentials with the driver.`);

      // Reset form
      setFormData({
        displayName: '',
        initialPassword: ''
      });
      setShowCreateForm(false);

      // Reload drivers list
      loadDrivers();

    } catch (error) {
      toast.dismiss();
      console.error('Error creating driver:', error);

      if (error.code === 'functions/already-exists') {
        toast.error('Username already exists. Please choose a different name.');
      } else if (error.code === 'functions/failed-precondition') {
        toast.error('Maximum driver accounts reached. Please contact info@skipeye.com to upgrade your package.');
      } else if (error.code === 'functions/permission-denied') {
        toast.error('Permission denied: Only admins can create driver accounts');
      } else if (error.code === 'functions/invalid-argument') {
        toast.error(error.message);
      } else {
        toast.error(`Failed to create driver: ${error.message}`);
      }
    }
  };

  const handleDeleteDriver = async (driver) => {
    if (!window.confirm(`Are you sure you want to delete driver "${driver.displayName}" (${driver.username})?\n\nThis will remove their access but NOT delete their delivery/collection records.`)) {
      return;
    }

    try {
      toast.loading('Deleting driver...');

      const deleteDriver = httpsCallable(functions, 'deleteDriver');
      await deleteDriver({ driverId: driver.id });

      toast.dismiss();
      toast.success('Driver deleted successfully');

      // Reload drivers list
      loadDrivers();
    } catch (error) {
      toast.dismiss();
      console.error('Error deleting driver:', error);

      if (error.code === 'functions/permission-denied') {
        toast.error('Permission denied: Only admins can delete driver accounts');
      } else if (error.code === 'functions/not-found') {
        toast.error('Driver not found');
      } else {
        toast.error(`Failed to delete driver: ${error.message}`);
      }
    }
  };

  const handlePasswordChange = async (driverId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      toast.loading('Changing password...');

      // Call Cloud Function
      const changePassword = httpsCallable(functions, 'changeDriverPassword');
      await changePassword({
        driverId: driverId,
        newPassword: newPassword
      });

      toast.dismiss();
      toast.success('Password changed successfully!');

      // Clear form
      setPasswordChangeDriverId(null);
      setNewPassword('');

    } catch (error) {
      toast.dismiss();
      console.error('Error changing password:', error);

      if (error.code === 'functions/permission-denied') {
        toast.error('Permission denied: Only admins can change passwords');
      } else if (error.code === 'functions/not-found') {
        toast.error('Driver not found');
      } else if (error.code === 'functions/invalid-argument') {
        toast.error(error.message);
      } else {
        toast.error(`Failed to change password: ${error.message}`);
      }
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, initialPassword: password });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#FAFAF9',
      paddingTop: '50px'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#292524',
      margin: 0,
      letterSpacing: '-0.025em'
    },
    createButton: {
      padding: '8px 16px',
      backgroundColor: '#166534',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 150ms ease'
    },
    limitWarning: {
      backgroundColor: '#FEE2E2',
      border: '1px solid #FECACA',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '2rem',
      fontSize: '14px',
      color: '#991B1B',
      textAlign: 'center'
    },
    formContainer: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #E7E5E4',
      boxShadow: '0 1px 3px rgba(28, 25, 23, 0.06)',
      marginBottom: '2rem'
    },
    formTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: '#292524',
      marginBottom: '1.25rem'
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: '500',
      color: '#78716C',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.025em'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      fontSize: '14px',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      boxSizing: 'border-box',
      color: '#292524',
      transition: 'border-color 150ms ease, box-shadow 150ms ease'
    },
    passwordInputGroup: {
      display: 'flex',
      gap: '0.5rem'
    },
    generateButton: {
      padding: '8px 12px',
      backgroundColor: 'white',
      color: '#44403C',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      whiteSpace: 'nowrap'
    },
    formActions: {
      display: 'flex',
      gap: '0.75rem',
      marginTop: '1.5rem'
    },
    submitButton: {
      padding: '8px 20px',
      backgroundColor: '#166534',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    cancelButton: {
      padding: '8px 20px',
      backgroundColor: 'white',
      color: '#44403C',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    driversGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '1rem'
    },
    driverCard: {
      backgroundColor: 'white',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #E7E5E4',
      boxShadow: '0 1px 3px rgba(28, 25, 23, 0.06)',
      transition: 'box-shadow 150ms ease'
    },
    driverName: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#292524',
      marginBottom: '0.25rem'
    },
    driverUsername: {
      fontSize: '14px',
      color: '#78716C',
      marginBottom: '1rem',
      fontFamily: 'monospace'
    },
    cardActions: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '1rem'
    },
    changePasswordButton: {
      flex: 1,
      padding: '6px 12px',
      backgroundColor: 'white',
      color: '#44403C',
      border: '1px solid #D6D3D1',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    deleteButton: {
      flex: 1,
      padding: '6px 12px',
      backgroundColor: '#FEE2E2',
      color: '#991B1B',
      border: '1px solid #FECACA',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem',
      color: '#78716C',
      fontSize: '1rem'
    },
    infoBox: {
      backgroundColor: '#F5F5F4',
      border: '1px solid #E7E5E4',
      borderRadius: '6px',
      padding: '1rem',
      marginBottom: '1.25rem',
      fontSize: '14px',
      color: '#57534E'
    }
  };

  if (loading) {
    return (
      <>
        <ViewToggle />
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.emptyState}>Loading drivers...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ViewToggle />
      <div style={styles.container}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>Driver Management</h1>
              {hasLimit && (
                <div style={{
                  fontSize: '14px',
                  color: isAtLimit ? '#DC2626' : '#78716C',
                  marginTop: '0.25rem',
                  fontWeight: '500'
                }}>
                  {currentDriverCount}/{maxDrivers} drivers {isAtLimit ? '(limit reached)' : ''}
                </div>
              )}
            </div>
            {!showCreateForm && (
              <button
                style={{
                  ...styles.createButton,
                  opacity: isAtLimit ? 0.5 : 1,
                  cursor: isAtLimit ? 'not-allowed' : 'pointer',
                  backgroundColor: isAtLimit ? '#A8A29E' : '#166534'
                }}
                onClick={() => {
                  if (isAtLimit) {
                    toast.error('Maximum driver accounts reached. Please contact info@skipeye.com to upgrade your package.');
                  } else {
                    setShowCreateForm(true);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!isAtLimit) e.target.style.backgroundColor = '#14532D';
                }}
                onMouseLeave={(e) => {
                  if (!isAtLimit) e.target.style.backgroundColor = '#166534';
                  else e.target.style.backgroundColor = '#A8A29E';
                }}
                disabled={isAtLimit}
              >
                + Create Driver
              </button>
            )}
          </div>

          {/* Limit Reached Warning */}
          {isAtLimit && !showCreateForm && (
            <div style={styles.limitWarning}>
              <strong>Maximum driver accounts reached.</strong>
              <br />
              Contact <a href="mailto:info@skipeye.com" style={{ color: '#166534', textDecoration: 'underline' }}>info@skipeye.com</a> to upgrade your package.
            </div>
          )}

          {/* Create Driver Form */}
          {showCreateForm && (
            <div style={styles.formContainer}>
              <h2 style={styles.formTitle}>Create New Driver</h2>

              <div style={styles.infoBox}>
                <strong>Note:</strong> Drivers will use their username and password to log in to the mobile app.
              </div>

              <form onSubmit={handleCreateDriver}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Driver Name</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="e.g., John, JohnL or JohnLennon"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    required
                  />
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Driver names must be unique. A first name alone (e.g. 'John') is fine unless you have multiple drivers with the same name.
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Username (Auto-generated)</label>
                  <input
                    type="text"
                    style={{ ...styles.input, backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    value={generatedUsername || ''}
                    readOnly
                    placeholder="Enter driver name above..."
                  />
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    This username will be used for login on the mobile app.
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Initial Password</label>
                  <div style={styles.passwordInputGroup}>
                    <input
                      type="text"
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="Minimum 6 characters"
                      value={formData.initialPassword}
                      onChange={(e) => setFormData({ ...formData, initialPassword: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      style={styles.generateButton}
                      onClick={generatePassword}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                    >
                      Generate
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', marginBottom: 0 }}>
                    You are responsible for setting a secure password. Avoid simple passwords like "password" or "123456".
                  </p>
                </div>

                <div style={styles.formActions}>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                  >
                    Create Driver
                  </button>
                  <button
                    type="button"
                    style={styles.cancelButton}
                    onClick={() => {
                      setShowCreateForm(false);
                      setFormData({ displayName: '', initialPassword: '' });
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Drivers List */}
          {drivers.length === 0 ? (
            <div style={styles.emptyState}>
              No drivers found. Click "Create Driver" to add your first driver.
            </div>
          ) : (
            <div style={styles.driversGrid}>
              {drivers.map(driver => (
                <div
                  key={driver.id}
                  style={styles.driverCard}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                >
                  <div style={styles.driverName}>{driver.displayName || 'Unnamed Driver'}</div>
                  <div style={styles.driverUsername}>{driver.username || 'no-username'} (username)</div>

                  <div style={styles.cardActions}>
                    <button
                      style={styles.changePasswordButton}
                      onClick={() => setPasswordChangeDriverId(driver.id)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                    >
                      Change Password
                    </button>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDeleteDriver(driver)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Password Change Modal */}
          {passwordChangeDriverId && (
            <>
              {/* Backdrop */}
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => {
                  setPasswordChangeDriverId(null);
                  setNewPassword('');
                }}
              >
                {/* Modal */}
                <div
                  style={{
                    backgroundColor: 'white',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    maxWidth: '500px',
                    width: '90%'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>
                    Change Driver Password
                  </h2>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={styles.label}>New Password</label>
                    <div style={styles.passwordInputGroup}>
                      <input
                        type="text"
                        style={{ ...styles.input, flex: 1 }}
                        placeholder="Minimum 6 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoFocus
                      />
                      <button
                        type="button"
                        style={styles.generateButton}
                        onClick={() => {
                          const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
                          let password = '';
                          for (let i = 0; i < 10; i++) {
                            password += chars.charAt(Math.floor(Math.random() * chars.length));
                          }
                          setNewPassword(password);
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                      >
                        Generate
                      </button>
                    </div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', marginBottom: 0 }}>
                      You are responsible for setting a secure password. Avoid simple passwords like "password" or "123456".
                    </p>
                  </div>

                  <div style={styles.formActions}>
                    <button
                      style={styles.submitButton}
                      onClick={() => handlePasswordChange(passwordChangeDriverId)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      Change Password
                    </button>
                    <button
                      style={styles.cancelButton}
                      onClick={() => {
                        setPasswordChangeDriverId(null);
                        setNewPassword('');
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DriverManagement;
